import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AccessibleDealership } from "@lumika/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@lumika/ui";
import { authApi } from "../api/auth";

/**
 * Slack-workspace-style dealership switcher for the nav. When the
 * signed-in person's Google account maps to a single dealership it renders
 * the name as plain text (no affordance). With several, it becomes a
 * picker that re-mints the session for the chosen dealership.
 */
export function DealershipSwitcher({
  accessibleDealerships,
  currentName,
}: {
  accessibleDealerships: AccessibleDealership[];
  currentName: string;
}) {
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState(false);
  const current = accessibleDealerships.find((d) => d.current);

  if (accessibleDealerships.length <= 1 || !current) {
    return (
      <div className="flex h-9 items-center px-2 text-sm font-semibold">
        <span className="truncate">{currentName}</span>
      </div>
    );
  }

  async function onSelect(dealershipId: string) {
    if (switching || dealershipId === current?.dealershipId) return;
    setSwitching(true);
    try {
      await authApi.switchDealership(dealershipId);
      // Every cached entry is dealership-scoped. Clear the cache and
      // hard-reload so nothing from the previous dealership can leak into
      // the new session's views.
      queryClient.clear();
      window.location.assign("/");
    } catch {
      setSwitching(false);
      toast.error("Could not switch dealership. Please try again.");
    }
  }

  return (
    <Select value={current.dealershipId} onValueChange={onSelect} disabled={switching}>
      <SelectTrigger
        aria-label="Switch dealership"
        title={current.dealershipName}
        className="min-h-touch w-full justify-start gap-1 rounded-md border-0 bg-transparent px-2 text-sm font-semibold shadow-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 data-[state=open]:bg-muted sm:h-9 sm:min-h-0 [&>span]:truncate"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {accessibleDealerships.map((d) => (
          <SelectItem key={d.dealershipId} value={d.dealershipId}>
            {d.dealershipName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
